import { useQuery } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import { Link } from '../../../../common/src/components/Link'
import { H4, Spacer, H5, IntroText, EllipsisSpan } from '../../../../common/src/components/core'
import { diffFmt } from '../../../../common/src/date'
import { lex } from '../../../../common/src/func'
import { FetchContactThreadLinksQuery, FetchContactThreadLinksQueryVariables } from '../../../../common/src/types.gen'
import { fetchContactThreadLinksQuery } from '../../graphql/queries/FetchContactThreadLinks'
import { TaskIcon } from '../components/task'
import { getThreadPath } from '../route'

type OtherThread = FetchContactThreadLinksQuery['contact']['threads'][0]

export default function ContactThreadLinks(props: { threadId: number; contactId: number }) {
  const res = useQuery<FetchContactThreadLinksQuery, FetchContactThreadLinksQueryVariables>(
    fetchContactThreadLinksQuery,
    {
      variables: { contactId: props.contactId },
    }
  )

  if (res.loading || res.error || res.data == null) {
    return null
  }

  const threads = res.data.contact.threads
    .filter(th => !!th.lastMessageTime)
    .sort(lex(th => th.lastMessageTime!))
    .reverse() as OtherThread[]

  if (threads.length === 1) {
    return null
  }

  const firms = threads.reduce((fs, t) => {
    const f = fs.find(ff => ff.name === t.leasingTask.firm.firmName)
    if (f) {
      f.threads.push(t)
    } else {
      fs.push({ name: t.leasingTask.firm.firmName, threads: [t] })
    }
    return fs
  }, [] as { name: string; threads: OtherThread[] }[])

  return (
    <div>
      <H4>Threads</H4>
      <Spacer height={16} />
      <div>
        {firms.map((firm, i) => (
          <div key={firm.name}>
            {firms.length > 1 && (
              <>
                {i !== 0 && <Spacer height={16} />}
                <H5>{firm.name}</H5>
              </>
            )}
            {firm.threads.map(t => (
              <div key={t.id}>
                <ThreadLink thread={t} isCurrent={t.id === props.threadId} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function ThreadLink({ thread, isCurrent }: { thread: OtherThread; isCurrent: boolean }) {
  const task = thread.leasingTask

  const tooltip = [thread.conversationType, thread.messageType, task.firm.firmName, task.displayName].join(' · ')

  return (
    <IntroText title={tooltip}>
      <Link to={isCurrent ? undefined : getThreadPath(thread.id)} color={isCurrent ? 'snow' : 'silver'}>
        <TaskIcon task={task} />
        <Spacer width={8} />
        <EllipsisSpan width="6em">{task.displayName}</EllipsisSpan>
        <Spacer width={8} />
        <MessageSpan width="208px">
          {diffFmt(new Date(), thread.lastMessageTime!)} ago &middot;{' '}
          <SnippetSpan>{thread.lastMessageSnippet}</SnippetSpan>
        </MessageSpan>
      </Link>
    </IntroText>
  )
}

const MessageSpan = styled(EllipsisSpan)`
  font-size: 14px;
`

const SnippetSpan = styled.span`
  font-style: italic;
`
